"use client";

import { memo, useCallback } from "react";
import {
  ArrowRightToLineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePdfViewMode } from "./pdfViewModeContext";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { openGeneratedDocumentInSidebar, openPreviewInSidebar } from "@/lib/threadDocumentOps";
import { getLiveThreadId } from "@/components/assistant";

// ─── Generated-doc header controls ──────────────────────────────────────────

/**
 * Version stepper for generated documents. Hidden when the doc has a single version.
 */
export const VersionStepper = memo(function VersionStepper({
  docId,
  title,
  activeVersionId,
}: {
  docId: string;
  title: string;
  activeVersionId?: string;
}) {
  const { t } = useTranslation();
  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);
  const versions = entry?.versions ?? [];
  const currentVersionId = entry?.currentVersionId;

  // All hooks must be called unconditionally before any early return to satisfy
  // the Rules of Hooks. Switching between a 1-version doc and a 2+-version doc
  // would otherwise change the hook count between renders (React error #310).
  const selectedVersionId = activeVersionId ?? entry?.selectedVersionId ?? currentVersionId;
  const selectedIdx = versions.findIndex((v) => v.versionId === selectedVersionId);
  const total = versions.length;
  const selectedVersion = selectedIdx >= 0 ? versions[selectedIdx] : undefined;
  const currentNumber = selectedVersion?.versionNumber ?? versions[total - 1]?.versionNumber ?? 1;

  const goTo = useCallback(
    (versionId: string) => {
      useGeneratedDocumentStore.getState().selectVersion(docId, versionId);
      const ver = versions.find((v) => v.versionId === versionId);
      // Pending versions open the preview in sidebar instead of the standard doc download.
      if (ver?.isPending && ver.previewPdfUrl) {
        void openPreviewInSidebar(versionId, title, getLiveThreadId(), docId);
      } else {
        void openGeneratedDocumentInSidebar(docId, title, getLiveThreadId(), versionId);
      }
    },
    [docId, title, versions],
  );

  const onPrev = useCallback(() => {
    const idx = Math.max(0, selectedIdx - 1);
    if (idx !== selectedIdx && versions[idx]) goTo(versions[idx].versionId);
  }, [goTo, selectedIdx, versions]);

  const onNext = useCallback(() => {
    const idx = Math.min(total - 1, selectedIdx + 1);
    if (idx !== selectedIdx && versions[idx]) goTo(versions[idx].versionId);
  }, [goTo, selectedIdx, total, versions]);

  const onJumpToCurrent = useCallback(() => {
    if (currentVersionId && currentVersionId !== selectedVersionId) goTo(currentVersionId);
  }, [currentVersionId, selectedVersionId, goTo]);

  if (versions.length <= 1) return null;

  const showJump = currentVersionId && currentVersionId !== selectedVersionId;

  return (
    <div className="flex items-center gap-0.5">
      <TooltipIconButton
        tooltip={t("pdfToolbar.previousVersion")}
        className="size-6 rounded-md cursor-pointer hover:bg-muted"
        onClick={onPrev}
        disabled={selectedIdx <= 0}
      >
        <ChevronLeftIcon className="size-3" />
      </TooltipIconButton>
      <span className="tabular-nums px-0.5 text-[11px] text-muted-foreground">
        {selectedVersion?.isPending
          ? t("pdfToolbar.versionLabelPending", { n: currentNumber, total })
          : t("pdfToolbar.versionLabel", { n: currentNumber, total })}
      </span>
      <TooltipIconButton
        tooltip={t("pdfToolbar.nextVersion")}
        className="size-6 rounded-md cursor-pointer hover:bg-muted"
        onClick={onNext}
        disabled={selectedIdx >= total - 1}
      >
        <ChevronRightIcon className="size-3" />
      </TooltipIconButton>
      {showJump && (
        <TooltipIconButton
          tooltip={t("pdfToolbar.backToCurrent")}
          className="size-6 rounded-md cursor-pointer hover:bg-muted"
          onClick={onJumpToCurrent}
        >
          <ArrowRightToLineIcon className="size-3" />
        </TooltipIconButton>
      )}
    </div>
  );
});

/**
 * Segmented [PDF | Diff] toggle for generated documents. The Diff button is
 * disabled when the active version has no rendered diff (v1 always; vN+1 only
 * when the LDOM apply-edits path persisted one).
 *
 * Both views share the same content slot in the PdfPanel — this just flips a
 * mode flag; the parent renders <see cref="PdfViewerCore"/> or
 * <see cref="PdfDiffView"/> accordingly. The diff itself is fetched lazily on
 * first selection and cached per (docId, versionId) on the pdfDocumentStore.
 */
export const PdfDiffToggle = memo(function PdfDiffToggle({
  docId,
  versionId,
}: {
  docId: string;
  versionId?: string;
}) {
  const { t } = useTranslation();
  const { viewMode, setViewMode } = usePdfViewMode();

  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);
  const targetVersionId = versionId ?? entry?.selectedVersionId ?? entry?.currentVersionId;
  const targetVersion = entry?.versions?.find((v) => v.versionId === targetVersionId);
  // Diff is available for committed versions with hasDiff, or pending versions with a diff URL.
  const hasDiff = targetVersion?.hasDiff === true || (targetVersion?.isPending && !!targetVersion.previewDiffHtmlUrl);

  return (
    <div className="flex items-center rounded-lg bg-muted/60 p-0.5">
      <button
        type="button"
        onClick={() => setViewMode("pdf")}
        className={cn(
          "rounded-md px-2.5 py-1 text-[11px] font-medium transition-all cursor-pointer",
          viewMode === "pdf"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {t("pdfToolbar.viewPdf")}
      </button>
      <button
        type="button"
        onClick={() => hasDiff && setViewMode("diff")}
        disabled={!hasDiff}
        title={hasDiff ? undefined : t("pdfToolbar.diffUnavailable")}
        className={cn(
          "rounded-md px-2.5 py-1 text-[11px] font-medium transition-all",
          hasDiff
            ? viewMode === "diff"
              ? "bg-background text-foreground shadow-sm cursor-pointer"
              : "text-muted-foreground hover:text-foreground cursor-pointer"
            : "text-muted-foreground/40 cursor-not-allowed",
        )}
      >
        {t("pdfToolbar.viewDiff")}
      </button>
    </div>
  );
});

